import {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {diApi} from '@di-shared/api/di';
import {
  ApiResponseDemeritEducationApiResponseDemeritEducationEligibilityResponse,
  type ApiResponseDemeritEducationApiResponseListDemeritEducationReserveResponse,
} from '@di-shared/api/di/__generated';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {WebviewUtils} from '@shared/webviewUtils';
import {useSuspenseQueries} from '@tanstack/react-query';

export interface DemeritEducationContentsProps {
  vin: string;
  isAgree: boolean;
}

function DemeritEducationContents({vin, isAgree}: DemeritEducationContentsProps) {
  const {t} = useInferableTranslation();

  return isAgree ? (
    <ErrorBoundary fallback={<></>}>
      <Suspense
        fallback={
          <li className="application_item skeleton">
            <div className="title_area">
              <strong className="title">{t('T10240')}</strong>
            </div>
            <p className="desc">{t('T10242')}</p>
            <div className="btn_box">
              <button
                type="button"
                className="btn btn_large btn_filled btn_full"
              >
                {t('T10243')}
              </button>
            </div>
          </li>
        }
      >
        <AgreeInner vin={vin} />
      </Suspense>
    </ErrorBoundary>
  ) : (
    <li className="application_item">
      <LinkTo
        className="link"
        to={ROUTE_PATHS.PUBLIC_SERVICE_DEMERIT_EDUCATION_ABOUT.getFullPath(vin)}
      >
        <div className="application_text">
          <strong className="title">{t('T10240')}</strong>
          <p className="desc">{t('T10271')}</p>
        </div>
        <span className="ic ic_arrow_forward_16px" />
      </LinkTo>
    </li>
  );
}

function AgreeInner({vin}: {vin: string}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {openDINewStackWebView} = useCcWebviewUtilsHelper();

  const [{data: reserveData}, {data: eligibilityData}] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApi.demeritEducationController.getReserve)],
        queryFn: () => diApi.demeritEducationController.getReserve({}),
        select: ({data}: ApiResponseDemeritEducationApiResponseListDemeritEducationReserveResponse) => data,
        refetchOnWindowFocus: true,
      },
      {
        queryKey: [getQueryKey(diApi.demeritEducationController.getEligibility)],
        queryFn: () => diApi.demeritEducationController.getEligibility({}),
        select: ({data}: ApiResponseDemeritEducationApiResponseDemeritEducationEligibilityResponse) => data,
        refetchOnWindowFocus: true,
      },
    ],
  });

  return (
    <>
      {(() => {
        // 교육 예약 완료
        if (reserveData?.resultCode === 'R00') {
          const reserveContent = reserveData.contents[0]; // 예약은 단일 건 존재
          const eduDate = parseDateWithFormat(reserveContent.eduDate);
          const startTime = parseDateWithFormat(reserveContent.eduDate + reserveContent.startTime);
          const endTime = parseDateWithFormat(reserveContent.eduDate + reserveContent.endTime);
          if (!eduDate || !startTime || !endTime) {
            throw new Error('demerit education date error');
          }
          return (
            <li className="application_item asset_after">
              <div className="title_area">
                <strong className="title">{t('T10240')}</strong>
              </div>
              <div className="badge_box">
                <span className="badge badge_basic_container">{t('T10265')}</span>
              </div>
              <dl className="vehicle_info">
                <div className="vehicle_item">
                  <dt className="info_title">{t('T10261')}</dt>
                  <dd className="info_text">{reserveContent.name}</dd>
                </div>
                <div className="vehicle_item">
                  <dt className="info_title">{t('T10262')}</dt>
                  <dd className="info_text">{CldrDatetimeFormatter.format(eduDate, 'yMMMd')}</dd>
                </div>
                <div className="vehicle_item">
                  <dt className="info_title">{t('T10263')}</dt>
                  <dd className="info_text">
                    {CldrDatetimeFormatter.format(startTime, 'aHM')} ~ {CldrDatetimeFormatter.format(endTime, 'aHM')}
                  </dd>
                </div>
              </dl>
              <div className="btn_box">
                <button
                  type="button"
                  className="btn btn_large btn_filled_gray btn_full"
                  onClick={() => {
                    openDINewStackWebView(ROUTE_PATHS.PUBLIC_SERVICE_DEMERIT_EDUCATION_RESERVATION_EDIT.getFullPath(vin));
                  }}
                >
                  {t('T10264')}
                </button>
              </div>
            </li>
          );
          // 교육 예약 대상자
        } else if (eligibilityData?.resultCode === 'S00') {
          return (
            <li className="application_item asset_after">
              <div className="title_area">
                <strong className="title">{t('T10240')}</strong>
              </div>
              <p className="desc">{t('T10242')}</p>
              <div className="btn_box">
                <button
                  type="button"
                  className="btn btn_large btn_filled btn_full"
                  onClick={() => {
                    openDINewStackWebView(ROUTE_PATHS.PUBLIC_SERVICE_DEMERIT_EDUCATION_RESERVATION.getFullPath(vin));
                  }}
                >
                  {t('T10243')}
                </button>
              </div>
            </li>
          );
          // 교육 예약 미대상자
        } else if (eligibilityData === null || eligibilityData.resultCode === 'S02' || eligibilityData.resultCode === 'S07') {
          return (
            <li className="application_item asset_after">
              <div className="title_area">
                <strong className="title">{t('T10240')}</strong>
              </div>
              <p className="desc">{t('T10244')}</p>
              <div className="guide_box">
                <p className="desc">{t('T10310')}</p>
                <p className="desc">{t('T10311')}</p>
              </div>
            </li>
          );
          // CI 정보 없음
        } else if (eligibilityData?.resultCode === 'S01') {
          return (
            <li className="application_item asset_after">
              <div className="title_area">
                <strong className="title">{t('T10240')}</strong>
              </div>
              <p className="desc">{t('T10245')}</p>
              <div className="guide_box">
                <p className="desc">{t('T10246')}</p>
              </div>
              <div className="btn_box">
                <button
                  type="button"
                  className="btn btn_large btn_filled btn_full"
                  onClick={() => WebviewUtils.openWebBrowser(import.meta.env.VITE_SAFE_DRIVING_MOBILE_URL)}
                >
                  {t('T10248')}
                </button>
              </div>
            </li>
          );
        }
      })()}
    </>
  );
}

export default DemeritEducationContents;
